import { useState, useEffect, forwardRef  } from "react";
import {
  Card,
  Table,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Button,
  Dropdown,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import api from "../../services/api";
import { formataDataHoraExibicao, getCurrentUser } from "../../common/utils";
import { isSuperUser } from "../../common/userUtils";
import { FaSearch, FaEraser, FaEllipsisV, FaTrash } from "react-icons/fa";
import moment from "moment-timezone";
import StatusBadge from "./StatusBadge";
import { excluirReporteFarmaco } from "./excluirReporte";
import Paginacao from "../../common/pagination";

const CustomToggle = forwardRef(({ children, onClick }, ref) => (
  <Button
    variant="link"
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="text-dark p-0" 
    style={{ textDecoration: 'none' }}
  >
    <FaEllipsisV size={20} />
    {children}
  </Button>
));

CustomToggle.displayName = 'CustomToggle';

const NavegadoraPage = () => {
  const [reportes, setReportes] = useState({
    results: [],
    count: 0,
    next: null,
    previous: null,
  });
  const [page_numb, setPageNumb] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const dados_usuario = getCurrentUser();
  const ambiente_cliente_id = dados_usuario?.ambiente_cliente_id;
  const ambiente_programa_id = dados_usuario?.ambiente_programa_id;
  // Superuser (ou usuário sem programa fixo no ambiente) precisa escolher o programa manualmente
  const precisaSelecionarPrograma = !ambiente_programa_id;

  const initialDataInicio = moment().startOf("month").format("YYYY-MM-DD");
  const initialDataFim = moment().endOf("month").format("YYYY-MM-DD");

  const [filtroDataInicio, setFiltroDataInicio] = useState(initialDataInicio);
  const [filtroDataFim, setFiltroDataFim] = useState(initialDataFim);
  const [filtroReporteId, setFiltroReporteId] = useState("");
  const [filtroAtendimentoId, setFiltroAtendimentoId] = useState("");
  // Valores efetivamente aplicados na busca (só mudam ao clicar em "Filtrar")
  const [buscaReporteId, setBuscaReporteId] = useState("");
  const [buscaAtendimentoId, setBuscaAtendimentoId] = useState("");

  const [filtroClienteId, setFiltroClienteId] = useState("");
  const [filtroClienteNome, setFiltroClienteNome] = useState("");
  const [filtroProgramaId, setFiltroProgramaId] = useState("");
  const [filtroProgramaNome, setFiltroProgramaNome] = useState("");
  const [programaSelectKey, setProgramaSelectKey] = useState(0);
  // Incrementado apos excluir um reporte para forcar a releitura da listagem
  const [recarregarLista, setRecarregarLista] = useState(0);

  // Usa o programa do ambiente quando existir; caso contrário, o selecionado no filtro (superuser)
  const programaIdEfetivo = ambiente_programa_id || filtroProgramaId;

  useEffect(() => {
    const consultaReportes = async () => {
      setLoading(true);
      setError("");

      if (!programaIdEfetivo) {
        setLoading(false);
        setReportes({ results: [], count: 0 });
        return;
      }

      try {
        let filtros = `?data_inicio=${filtroDataInicio}&data_fim=${filtroDataFim}&page=${page_numb}&programa_id=${programaIdEfetivo}`;
        if (buscaReporteId) filtros += `&id=${buscaReporteId}`;
        if (buscaAtendimentoId) filtros += `&atendimento_id=${buscaAtendimentoId}`;
        const response = await api.get(`/formularios/respostas/${filtros}`);
        setReportes(response.data);
      } catch (error) {
        console.error("Erro ao buscar reportes:", error);
        setError("Ocorreu um erro ao buscar os reportes. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    consultaReportes();
  }, [
    filtroDataInicio,
    filtroDataFim,
    page_numb,
    programaIdEfetivo,
    buscaReporteId,
    buscaAtendimentoId,
    recarregarLista,
  ]);

  const carregarClientes = async (inputValue) => {
    try {
      const response = await api.get(`/pessoas/clientes/?nome=${inputValue}`);
      return response.data.results.map((cliente) => ({
        value: cliente.id,
        label: `${cliente.dados_pessoa.nome_fantasia} (${cliente.dados_pessoa.razao_social})`,
      }));
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      return [];
    }
  };

  const carregaProgramas = async (inputValue) => {
    if (!filtroClienteId) {
      return [];
    }
    try {
      const response = await api.get(
        `/programas/?nome=${inputValue}&cliente_id=${filtroClienteId}`
      );
      return response.data.results.map((programa) => ({
        value: programa.id,
        label: programa.nome,
      }));
    } catch (error) {
      console.error("Erro ao carregar programas:", error);
      return [];
    }
  };

  const handleClienteChange = (opcao) => {
    setFiltroClienteId(opcao ? opcao.value : "");
    setFiltroClienteNome(opcao ? opcao.label : "");
    setFiltroProgramaId("");
    setFiltroProgramaNome("");
    setProgramaSelectKey((k) => k + 1);
    setPageNumb(1);
  };

  const handleProgramaChange = (opcao) => {
    setFiltroProgramaId(opcao ? opcao.value : "");
    setFiltroProgramaNome(opcao ? opcao.label : "");
    setPageNumb(1);
  };

  const handleFiltrar = (e) => {
    e.preventDefault();
    setBuscaReporteId(filtroReporteId.trim());
    setBuscaAtendimentoId(filtroAtendimentoId.trim());
    setPageNumb(1);
  };

  const limparFiltros = () => {
    setFiltroDataInicio(initialDataInicio);
    setFiltroDataFim(initialDataFim);
    setFiltroReporteId("");
    setFiltroAtendimentoId("");
    setBuscaReporteId("");
    setBuscaAtendimentoId("");
    if (precisaSelecionarPrograma) {
      setFiltroClienteId("");
      setFiltroClienteNome("");
      setFiltroProgramaId("");
      setFiltroProgramaNome("");
      setProgramaSelectKey((k) => k + 1);
    }
    setPageNumb(1);
  };

  const onClickPagina = (num_page) => {
    setPageNumb(num_page);
  };

  const handleReportarEventoAdverso = (reporte) => {
    const atendimentoUuid = reporte.dados_atendimento?.uuid;
    const pacienteId = reporte.dados_beneficiario?.id;
    const reporteId = reporte.id;
    const ano = new Date().getFullYear();

    if (!atendimentoUuid || !pacienteId) {
      setError(
        "Não foi possível iniciar o reporte. Faltam dados de atendimento ou paciente."
      );
      return;
    }

    // Regra solicitada pela Sawana da Aditus (número ID paciente + número do relato + ano)
    const reportRef = `${pacienteId}-${reporteId}-${ano}`;
    
    /*const navigationState = {
      state: {
        openPTC: true,
        reportRef: reportRef,
      },
    };*/

    const url = `/atendimentos/edit/${atendimentoUuid}?openPTC=true&reportRef=${reportRef}`;

    window.open(url, "_blank");

    //navigate(`/atendimentos/edit/${atendimentoUuid}`, navigationState);
  };

  const handleExcluirReporte = async (reporte) => {
    const excluido = await excluirReporteFarmaco(reporte);
    if (excluido) {
      setRecarregarLista((k) => k + 1);
    }
  };

  const renderActionButton = (reporte) => {
    let acoes = [];

    switch (reporte.status) {
      case "enviado":
        acoes.push(
          <Dropdown.Item
            key="visualizar"
            onClick={() => navigate(`/reportes-farmaco/detalhe/${reporte.id}`)}
          >
            Visualizar
          </Dropdown.Item>
        );
        break;
      case "em_analise":
        acoes.push(
          <Dropdown.Item
            key="continuar"
            onClick={() =>
              navigate(
                `/reportes-farmaco/editar/${reporte.copia_de_trabalho_id}`
              )
            }
          >
            Continuar Análise
          </Dropdown.Item>,
          <Dropdown.Item
            key="ver_original"
            onClick={() =>
              navigate(`/reportes-farmaco/detalhe/${reporte.id}`)
            }
          >
            Ver Original
          </Dropdown.Item>
        );
        break;
      case "analise_finalizada":
        acoes.push(
          <Dropdown.Item
            key="ver_final"
            onClick={() =>
              navigate(
                `/reportes-farmaco/detalhe/${reporte.copia_de_trabalho_id}`
              )
            }
          >
            Ver Versão Final
          </Dropdown.Item>
        );
        break;
      default:
        break;
    }

    if (reporte.dados_atendimento?.id) {
      if (acoes.length > 0) {
        acoes.push(<Dropdown.Divider key="divider" />);
      }
      
      acoes.push(
        <Dropdown.Item
          key="reportar_ea"
          onClick={() => handleReportarEventoAdverso(reporte)}
          className="text-primary"
        >
          Reportar Evento Adverso
        </Dropdown.Item>
      );
    }

    // Exclusao definitiva do reporte, liberada apenas para superadmin
    if (isSuperUser(dados_usuario)) {
      if (acoes.length > 0) {
        acoes.push(<Dropdown.Divider key="divider_excluir" />);
      }

      acoes.push(
        <Dropdown.Item
          key="excluir"
          onClick={() => handleExcluirReporte(reporte)}
          className="text-danger"
        >
          <FaTrash className="me-2" /> Excluir reporte
        </Dropdown.Item>
      );
    }

    if (acoes.length === 0) {
      return null;
    }

    return (
      <Dropdown>
        <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${reporte.id}`} />
        {/* strategy fixed: sem isso o menu fica preso ao scroll da tabela responsiva e aparece cortado */}
        <Dropdown.Menu
          align="end"
          renderOnMount
          popperConfig={{ strategy: "fixed" }}
        >
          {acoes}
        </Dropdown.Menu>
      </Dropdown>
    );
  };


  return (
    <div>
      <Row className="align-items-center">
        <Col sm={6} lg={6}>
          <h4 className="page-title">
            Reportes de Farmacovigilância Recebidos
          </h4>
        </Col>
      </Row>
      <Card className="cardDestaque">
        <Card.Body>
          <Form onSubmit={handleFiltrar} className="mb-4">
            <h4>Filtros</h4>
            <Row className="mt-2">
              {precisaSelecionarPrograma && ambiente_cliente_id === "" && (
                <Col xs={12} sm={6} lg={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Cliente</Form.Label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={carregarClientes}
                      name="cliente_id"
                      placeholder="Buscar cliente..."
                      noOptionsMessage={() => "Nenhum cliente encontrado"}
                      onChange={handleClienteChange}
                      value={
                        filtroClienteId
                          ? { value: filtroClienteId, label: filtroClienteNome }
                          : null
                      }
                    />
                  </Form.Group>
                </Col>
              )}
              {precisaSelecionarPrograma && filtroClienteId && (
                <Col xs={12} sm={6} lg={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Programa</Form.Label>
                    <AsyncSelect
                      key={programaSelectKey}
                      cacheOptions
                      defaultOptions
                      loadOptions={carregaProgramas}
                      name="programa_id"
                      placeholder="Buscar programa..."
                      noOptionsMessage={() => "Nenhum programa encontrado"}
                      onChange={handleProgramaChange}
                      value={
                        filtroProgramaId
                          ? { value: filtroProgramaId, label: filtroProgramaNome }
                          : null
                      }
                    />
                  </Form.Group>
                </Col>
              )}
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Data Início</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtroDataInicio}
                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Data Fim</Form.Label>
                  <Form.Control
                    type="date"
                    value={filtroDataFim}
                    onChange={(e) => setFiltroDataFim(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>ID do Reporte</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="Ex: 123"
                    value={filtroReporteId}
                    onChange={(e) => setFiltroReporteId(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>ID do Atendimento</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="Ex: 456"
                    value={filtroAtendimentoId}
                    onChange={(e) => setFiltroAtendimentoId(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              type="submit"
              variant="secondary"
              size="sm"
              className="mt-3"
            >
              <FaSearch /> Filtrar
            </Button>
            <Button
              type="button"
              variant="light"
              size="sm"
              className="mt-3 ms-2"
              onClick={limparFiltros}
            >
              <FaEraser /> Limpar
            </Button>
          </Form>
        </Card.Body>
      </Card>
      <Card className="cardDestaque">
        <Card.Body className="pt-0">
          {error && <Alert variant="danger">{error}</Alert>}
          {precisaSelecionarPrograma && !programaIdEfetivo && (
            <Alert variant="info" className="mt-3">
              Selecione um <strong>Cliente</strong> e um{" "}
              <strong>Programa</strong> nos filtros acima para visualizar os
              reportes.
            </Alert>
          )}
          <Table responsive className="custom-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Data de Envio</th>
                <th>Prestador</th>
                <th>Programa</th>
                <th>Paciente</th>
                <th>Atendimento Vinculado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : reportes.results && reportes.results.length > 0 ? (
                reportes.results.map((reporte) => (
                  <tr key={reporte.id}>
                    <td>{reporte.id}</td>
                    <td>
                      <StatusBadge status={reporte.status} />
                    </td>
                    <td>{formataDataHoraExibicao(reporte.data_cadastro)}</td>
                    <td>{reporte.dados_prestador?.nome || "-"}</td>
                    <td>{reporte.dados_programa?.nome || "-"}</td>
                    <td>
                      {reporte.dados_beneficiario?.nome ||
                        reporte.respostas?.paciente_nome_iniciais ||
                        "-"}
                    </td>
                    <td>{reporte.dados_atendimento?.id || "-"}</td>
                    <td>{renderActionButton(reporte)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    Nenhum reporte encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {reportes.results && reportes.results.length > 0 && (
            <Paginacao
              dados={reportes}
              loading={loading}
              page_numb={page_numb}
              _onClickPagina={onClickPagina}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NavegadoraPage;
